/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Hooks
import { useMeetings } from '../../hooks/meetings.hook';

// Components
import { MeetingCard } from '../MeetingCard';

// Types
import { GroupedMeetings, Meeting, MeetingStatus } from '@/types/meeting';

// Helpers
import { groupMeetingsByDate } from '@/helpers/date.helper';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
function CanceledMeetings() {
  const { t } = useTranslation();
  const { data, fetchNextPage, hasNextPage, isFetching, isPending } =
    useMeetings({
      params: {
        status: MeetingStatus.CANCELED,
      },
    });

  const meetings = useMemo<Meeting[]>(
    () => data?.pages.flatMap((item) => item.results) || [],
    [data]
  );

  const groupedMeetings = useMemo<GroupedMeetings>(
    () => groupMeetingsByDate(meetings),
    [meetings]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const observerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !hasNextPage) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            handleLoadMore();
          }
        },
        { threshold: 1.0 }
      );

      observer.observe(node);
      return () => observer.disconnect();
    },
    [hasNextPage, handleLoadMore]
  );

  const isMeetingsEmpty = Object.keys(groupedMeetings).length === 0;

  return (
    <div className="space-y-8">
      {isMeetingsEmpty && !isPending ? (
        <div className="text-center text-background-1">
          {t('pages.meetings.no_canceled_meetings')}
        </div>
      ) : (
        Object.entries(groupedMeetings).map(([date, meetings]) => (
          <div key={date}>
            <h2 className="text-lg font-semibold text-background-1 mb-4">
              {t(date)}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {meetings.map((meeting) => (
                <MeetingCard key={meeting.id} meeting={meeting} />
              ))}
            </div>
          </div>
        ))
      )}
      <div ref={observerRef} className="text-center py-4"></div>
    </div>
  );
}

export default CanceledMeetings;
